import { Component, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { User } from 'src/app/models/user.model';
import { MatPaginator } from '@angular/material/paginator';
import { switchMap, map, catchError, tap, withLatestFrom } from 'rxjs/operators';
import { UsersService } from 'src/app/services/users.service';
import { of, BehaviorSubject, merge, Observable } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { UserDialogComponent } from '../user-dialog/user-dialog.component';
import { ConfirmationService } from 'src/app/lib/services/confirmation.service';
import { ToastsService } from 'src/app/lib/services/toasts.service';
import { MatSort, Sort } from '@angular/material/sort';
import { faGhost } from '@fortawesome/free-solid-svg-icons';
import { CollectionResponse } from 'src/app/models/api.model';
import { Router } from '@angular/router';
import { Filter } from 'src/app/modules/filtering/models';

// A query is an unambiguous identifier when it can only have been pasted, not
// typed as a name: all digits (a user id) or something holding an @ (an email).
const isUserIdentifier = (query: string) => /^\d+$/.test(query) || query.includes('@');

@Component({
  standalone: false,
  selector: 'sa-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss']
})
export class UsersComponent implements OnInit, AfterViewInit {

  constructor(
    public dialog: MatDialog,
    public usersService: UsersService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private toastsService: ToastsService
  ) { }

  faGhost = faGhost;

  hideMockUsers = new BehaviorSubject<boolean>(false);
  hideBannedCountries = new BehaviorSubject<boolean>(false);
  showDeletedUsers = new BehaviorSubject<boolean>(false);
  incompleteOnboarding = new BehaviorSubject<boolean>(false);
  /**
   * Only profiles with enough swipes for the like/dislike ratio to mean
   * anything. The list opens on the newest signups, who nobody has swiped on
   * yet, so without this the ratio column and its colour are blank for a whole
   * page even though thousands of rated accounts exist.
   */
  ratedOnly = new BehaviorSubject<boolean>(false);

  // Which kind of profile to list. null = both, true = leads with a playable
  // video, false = photo-only. Jowa's deck treats the two differently, so
  // reviewing one kind at a time is a real need.
  mediaFilter = new BehaviorSubject<boolean | null>(null);
  query = new BehaviorSubject<string>('');
  // Seeded in ngOnInit (see below) so subclass field overrides are already in place.
  filters = new BehaviorSubject<Filter[]>([]);
  sortChange = new BehaviorSubject<Sort>({ active: 'createdAt', direction: 'desc' });

  data: User[] = [];

  // Stable references so child @Inputs don't see a new array/object every CD cycle.
  // getDisplayedColumns()/getSort() below return these; subclasses override the fields.
  readonly displayedColumns: string[] = ['profile_image', 'actions', 'user_id', 'name', 'email', 'birth', 'gender', 'media', 'is_mock_user', 'country', 'platform', 'login_method', 'app_version', 'report_count', 'like_dislike_ratio', 'given_like_dislike_ratio', 'createdAt', 'last_activity', 'active', 'subscription_tier'];
  readonly initialSort: Sort = { active: 'createdAt', direction: 'desc' };

  resultsLength = 0;
  isLoading = true;
  hasError = false;
  limit = 30;
  pageSizeOptions = [30, 100, 200, 500];

  @ViewChild(MatPaginator) paginator: MatPaginator;

  ngOnInit() {
    // Seed here, not in field initializers, so subclass overrides of the stable
    // fields are already applied — and not in ngAfterViewInit, where pushing a
    // new filter array after the view is checked raises NG0100 on every page
    // that overrides getFilter() (banned, under review, silent hidden).
    this.sortChange.next(this.getSort());
    this.filters.next(this.getFilter());
  }

  /** The media toggle group emits '', 'video' or 'photo'. */
  mediaFilterChanged(value: string): void {
    this.paginator.pageIndex = 0;
    if (value === 'video') {
      this.mediaFilter.next(true);
    } else if (value === 'photo') {
      this.mediaFilter.next(false);
    } else {
      this.mediaFilter.next(null);
    }
  }

  hideMockUsersChanged(checked: boolean): void {
    this.paginator.pageIndex = 0;
    this.hideMockUsers.next(checked);
  }

  ngAfterViewInit() {
    merge(this.sortChange, this.paginator.page, this.query, this.filters, this.hideBannedCountries, this.showDeletedUsers, this.incompleteOnboarding, this.ratedOnly, this.mediaFilter, this.hideMockUsers).pipe(
      withLatestFrom(this.sortChange),
      switchMap(([event, sort]) => {
        this.isLoading = true;
        return this.usersService.getUsers(
          this.query.value,
          this.paginator.pageIndex + 1,
          this.paginator.pageSize || this.limit,
          `${sort.active}:${sort.direction}`,
          this.filters.value,
          this.hideBannedCountries.value,
          this.showDeletedUsers.value,
          this.incompleteOnboarding.value,
          this.ratedOnly.value,
          this.mediaFilter.value,
          this.hideMockUsers.value,
        ).pipe(
          switchMap(collection => this.withIdentifierFallback(collection))
        );
      }),
      map(usersCollection => {
        // Flip flag to show that loading has finished.
        this.isLoading = false;
        this.hasError = false;
        this.resultsLength = usersCollection.totalCount;

        return usersCollection.results;
      }),
      catchError(() => {
        this.isLoading = false;
        this.hasError = true;
        return of([]);
      })
    ).subscribe(data => this.data = data);
  }

  /**
   * An admin who pastes a user id or an email address wants that exact account,
   * but the list request above still hides deleted rows, so a deleted user
   * comes back empty. When the list finds nothing and the query can only be an
   * identifier, retry through the direct lookup endpoint, which ignores both.
   *
   * Skipped while any filter is active — that includes the Banned / Under
   * review / Silent hidden pages, which are this component with a seeded
   * filter. There, an empty result is the answer: the user is not in that set.
   */
  private withIdentifierFallback(collection: CollectionResponse<User>): Observable<CollectionResponse<User>> {
    const query = (this.query.value || '').trim();

    if (collection.totalCount > 0 || this.filters.value.length > 0 || this.hideMockUsers.value || !isUserIdentifier(query)) {
      return of(collection);
    }

    return this.usersService.searchUsers(
      query,
      this.paginator.pageIndex + 1,
      this.paginator.pageSize || this.limit,
    ).pipe(
      // The lookup is a courtesy retry — if it fails, keep the empty list
      // rather than turning a "no results" into an error banner.
      catchError(() => of(collection))
    );
  }

  getDisplayedColumns(): string[] {
    return this.displayedColumns;
  }

  getFilter(): Filter[] {
    return [];
  }

  getSort(): Sort {
    return this.initialSort;
  }

  sortChanged(e) {
    this.sortChange.next(e);
  }

  queryChanged(e) {
    this.paginator.pageIndex = 0;
    this.query.next(e.target.value);
  }

  pageChanged(e) {
    this.paginator.pageIndex = +e.target.value - 1;
    this.query.next(this.query.value);
  }

  onFiltersUpdated(filters) {
    this.filters.next(filters);
  }

  userClicked(user) {
    const dialogRef = this.dialog.open(UserDialogComponent, {
      data: {
        user
      },
      minWidth: 400,
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log(`Dialog result: ${result}`);

      if (result === 'edit') {
        this.router.navigate(['/users', user.user_id]);
      }
    });
  }

  userBanClicked(user) {
    this.usersService.banUser(user).pipe(
      tap((result) => {
        this.toastsService.showSuccess('Banned user successfully.');
        this.query.next(this.query.value);
      }),
      catchError((error) => {
        this.toastsService.showDanger('Error banning user: ' + JSON.stringify(error));
        return of([]);
      })
    ).subscribe();
  }

  userUnBanClicked(user) {
    this.confirmationService.askForConfirmation(`Unban ${user.name} ${user.lastname}?`, `Are you sure you want to unban ${user.name} ${user.lastname}?`)
      .then(() => {
        this.usersService.unBanUser(user).pipe(
          tap((result) => {
            this.toastsService.showSuccess('Unbanned user successfully.');
            this.query.next(this.query.value);
          }),
          catchError((error) => {
            this.toastsService.showDanger('Error unbanning user: ' + JSON.stringify(error));
            return of([]);
          })
        ).subscribe();
      })
      .catch(() => {});
  }

  userPutUnderReviewClicked(user) {
    this.usersService.putUserUnderReview(user).pipe(
      tap((result) => {
        this.toastsService.showSuccess('Put user under review successfully.');
        this.query.next(this.query.value);
      }),
      catchError((error) => {
        this.toastsService.showDanger('Error putting user under review: ' + JSON.stringify(error));
        return of([]);
      })
    ).subscribe();
  }

  userRemoveFromReviewClicked(user) {
    this.confirmationService.askForConfirmation(`Remove ${user.name} ${user.lastname} from review?`, `Are you sure you want to remove ${user.name} ${user.lastname} from review?`)
      .then(() => {
        this.usersService.removeUserFromReview(user).pipe(
          tap((result) => {
            this.toastsService.showSuccess('Removed user from review successfully.');
            this.query.next(this.query.value);
          }),
          catchError((error) => {
            this.toastsService.showDanger('Error removing user from review: ' + JSON.stringify(error));
            return of([]);
          })
        ).subscribe();
      })
      .catch(() => {});
  }

  userSilentHideClicked(user) {
    this.usersService.silentHideUser(user).pipe(
      tap((result) => {
        this.toastsService.showSuccess('Silent hid user successfully.');
        this.query.next(this.query.value);
      }),
      catchError((error) => {
        this.toastsService.showDanger('Error silent hiding user: ' + JSON.stringify(error));
        return of([]);
      })
    ).subscribe();
  }

  userSilentUnhideClicked(user) {
    this.confirmationService.askForConfirmation(`Unhide ${user.name} ${user.lastname}?`, `Are you sure you want to unhide ${user.name} ${user.lastname}?`)
      .then(() => {
        this.usersService.silentUnhideUser(user).pipe(
          tap((result) => {
            this.toastsService.showSuccess('Unhid user successfully.');
            this.query.next(this.query.value);
          }),
          catchError((error) => {
            this.toastsService.showDanger('Error unhiding user: ' + JSON.stringify(error));
            return of([]);
          })
        ).subscribe();
      })
      .catch(() => {});
  }

  userBoostClicked(user) {
    this.usersService.boostUser(user).pipe(
      tap((result) => {
        this.toastsService.showSuccess('Boosted user successfully.');
        this.query.next(this.query.value);
      }),
      catchError((error) => {
        this.toastsService.showDanger('Error boosting user: ' + JSON.stringify(error));
        return of([]);
      })
    ).subscribe();
  }

  userDeboostClicked(user) {
    this.usersService.deboostUser(user).pipe(
      tap((result) => {
        this.toastsService.showSuccess('Deboost user successfully.');
        this.query.next(this.query.value);
      }),
      catchError((error) => {
        this.toastsService.showDanger('Error deboosting user: ' + JSON.stringify(error));
        return of([]);
      })
    ).subscribe();
  }

  userDeleteClicked(user) {
    this.confirmationService.askForConfirmation(`Delete ${user.name} ${user.lastname}?`, `Are you sure you want to delete ${user.name} ${user.lastname}?`)
      .then(() => {
        this.usersService.deleteUser(user).pipe(
          tap((result) => {
            this.toastsService.showSuccess('Deleted user successfully.');
            this.query.next(this.query.value);
          }),
          catchError((error) => {
            this.toastsService.showDanger('Error deleting user: ' + JSON.stringify(error));
            return of([]);
          })
        ).subscribe();
      })
      .catch(() => {});
  }

}
